import React, { useState, useEffect, useCallback } from 'react'
import styles from './Analytics.module.css'

import {
    getApplicationStatusChartData,
    getAverageReviewTimeData,
    getDesktopVsMobileApplicationsData,
    getDropOffData
} from '../../api/analytics';

import useFetchData from '../../hooks/useFetchData';
import ApplicationStatusChart from './ApplicationStatusChart';
import DesktopVsMobileApplications from './DesktopVsMobileApplications';
import AverageReviewTimeChart from './AverageReviewTimeChart';
import DropOff from './DropOff';


const Analytics = () => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [isValueZero, setIsValueZero] = useState(false);

    const {
        data: applicationStatusChartData,
        isLoading: isApplicationStatusLoading,
        errorMessage: applicationStatusError,
        fetchData: fetchApplicationStatusChartData,
    } = useFetchData(getApplicationStatusChartData);

    const {
        data: desktopVsMobileApplicationsData,
        isLoading: isDesktopVsMobileLoading,
        errorMessage: desktopVsMobileError,
        fetchData: fetchDesktopVsMobileApplicationsData,
    } = useFetchData(getDesktopVsMobileApplicationsData);

    const {
        data: averageReviewTimeData,
        isLoading: isAverageReviewTimeLoading,
        errorMessage: averageReviewTimeError,
        fetchData: fetchAverageReviewTimeData,
    } = useFetchData(getAverageReviewTimeData);

    const {
        data: dropOffData,
        isLoading: isDropOffLoading,
        errorMessage: dropOffError,
        fetchData: fetchDropOffData,
    } = useFetchData(getDropOffData);

    const LABELS = {
        pendingApplicationsCount: { label: "Pending", color: "#FFCC00" },
        approvedApplicationsCount: { label: "Approved", color: "#659330" },
        rejectedApplicationsCount: { label: "Rejected", color: "#DC4405" },
    };

    const chartDescriptions = {
        ApplicationStatusChart: "Displays real-time counts of applications that are pending, approved, or rejected.",
        DesktopVsMobileApplicationsChart: "Breaks down what proportion of users complete identity verification on desktop versus mobile.",
        AverageReviewTimeChart: "Shows the weekly average duration between an application’s submission to the Review Center and its manual adjudication.",
        DropOffChart: "Charts the number of users who successfully reach each stage of the IDV process for every month.",
    }

    const onPieEnter = useCallback((_, index) => {
        setActiveIndex(index);
    }, []);

    useEffect(() => {
        fetchApplicationStatusChartData();
        fetchDesktopVsMobileApplicationsData();
        fetchAverageReviewTimeData();
        fetchDropOffData();
    }, [])

    useEffect(() => {
        if (applicationStatusChartData.length > 0 && Object.values(applicationStatusChartData).every((value) => value === 0)) {
            setIsValueZero(true);
        } else {
            setIsValueZero(false);
        }
        console.log("Analytics Data: ", applicationStatusChartData)
    }, [applicationStatusChartData]);

    const pieChartData = Object.entries(applicationStatusChartData)
        .filter(([key]) => Object.keys(LABELS).includes(key))
        .map(([key, value]) => ({
            name: LABELS[key].label,
            value: value,
            color: LABELS[key].color,
        }));

    return (
        <div className={styles['analytics-container']}>
            <ApplicationStatusChart
                isLoading={isApplicationStatusLoading}
                errorMessage={applicationStatusError}
                isValueZero={isValueZero}
                activeIndex={activeIndex}
                onPieEnter={onPieEnter}
                pieChartData={pieChartData}
                chartDescription={chartDescriptions.ApplicationStatusChart}
            />

            <DesktopVsMobileApplications
                isLoading={isDesktopVsMobileLoading}
                errorMessage={desktopVsMobileError}
                isValueZero={isValueZero}
                data={desktopVsMobileApplicationsData}
                chartDescription={chartDescriptions.DesktopVsMobileApplicationsChart}
            />

            <AverageReviewTimeChart
                isLoading={isAverageReviewTimeLoading}
                errorMessage={averageReviewTimeError}
                isValueZero={isValueZero}
                data={averageReviewTimeData}
                chartDescription={chartDescriptions.AverageReviewTimeChart}
            />

            <DropOff
                isLoading={isDropOffLoading}
                errorMessage={dropOffError}
                isValueZero={isValueZero}
                data={dropOffData}
                chartDescription={chartDescriptions.DropOffChart}
            />
        </div>
    )
}

export default Analytics